import { useEffect, useMemo, useRef, useState } from "react";
import lmuBackgroundImage from "./photos/tlolmu.webp";
import partnersBackgroundImage from "./photos/partnerstlo.webp";
import aboutBackgroundImage from "./photos/aboutustlo.webp";
import managementBackgroundImage from "./photos/Managementtlo.png.webp";
import racesBackgroundImage from "./photos/tloraces.webp";
import avatarFallbackImage from "./avatar.webp";

type Language = "pl" | "en" | "zh";
type SectionId = "home" | "partners" | "about" | "team" | "achievements" | "contact";
type MemberCategory = "management" | "lmu" | "assetto";
type LocalizedText = Record<Language, string>;
type LocalizedList = Record<Language, string[]>;

const localizedText = (pl: string, en: string, zh: string): LocalizedText => ({ pl, en, zh });

type Member = {
  id: string;
  name: string;
  nickname: string;
  category: MemberCategory;
  isActive: boolean;
  role: LocalizedText;
  location: string;
  image: string;
  bio: LocalizedText;
  highlights: LocalizedList;
  favoriteTrack: string;
  favoriteCar: string;
  racingNumber: string;
  teamSince: string;
  social1Label: string;
  social1Href: string;
  social2Label: string;
  social2Href: string;
};

type Race = {
  id: string;
  raceTitle: string;
  date: string;
  leagueName: string;
  carClass: string;
  eventType: string;
  participants: string;
  resultBy: string;
  position: string;
  streamPlatform: string;
  streamUrl: string;
};

type Achievement = {
  id: string;
  eventName: string;
  leagueName: string;
  position: string;
  winners: string;
  category: string;
  game: string;
  car: string;
  background: string;
  date: string;
};

type CountdownProps = {
  targetDate: Date | null;
};

type Partner = {
  name: string;
  website: string;
  image?: string;
  description: LocalizedText;
  promoLabel?: LocalizedText;
  promoCode?: string;
  promoDiscount?: string;
};

type CsvRow = Record<string, string>;

const MEMBERS_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQyWwaCBXm3xSMrpGUs64aYHnk6UmTb3SguMXU2XuaOtL76Ga9eU7TxkJp6qJhDozQa8ZDItG_kempE/pub?gid=0&single=true&output=csv";
const ACHIEVEMENTS_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQyWwaCBXm3xSMrpGUs64aYHnk6UmTb3SguMXU2XuaOtL76Ga9eU7TxkJp6qJhDozQa8ZDItG_kempE/pub?gid=830001350&single=true&output=csv";
const RACES_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQyWwaCBXm3xSMrpGUs64aYHnk6UmTb3SguMXU2XuaOtL76Ga9eU7TxkJp6qJhDozQa8ZDItG_kempE/pub?gid=969080874&single=true&output=csv";

const DRIVER_APPLICATION_ENDPOINT = "PASTE_APPS_SCRIPT_URL_HERE";

const IMAGE_ASSETS = {
  logos: {
    team:
      "/logo.webp",
    partnerPxn: "/pxn1.webp",
    partnerSimnation:
      "/simnation1.webp",
  },
  flags: {
    pl: "https://flagcdn.com/w40/pl.png",
    en: "https://flagcdn.com/w40/gb.png",
    zh: "https://flagcdn.com/w40/cn.png",
  },
  sectionBackgrounds: {
    home: {
      landscape: "/hometlo.webp",
      portrait: "/hometlo.webp",
    },
    partners: {
      landscape: partnersBackgroundImage,
      portrait: partnersBackgroundImage,
    },
    about: {
      landscape: aboutBackgroundImage,
      portrait: aboutBackgroundImage,
    },
    team: {
      landscape: "https://images.unsplash.com/photo-1471478331149-c72f17e33c73?auto=format&fit=crop&w=2200&q=80",
      portrait: "https://images.unsplash.com/photo-1471478331149-c72f17e33c73?auto=format&fit=crop&w=1200&q=80",
    },
    results: {
      landscape: racesBackgroundImage,
      portrait: racesBackgroundImage,
    },
    achievements: {
      landscape: racesBackgroundImage,
      portrait: racesBackgroundImage,
    },
    contact: {
      landscape: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=2200&q=80",
      portrait: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=1200&q=80",
    },
  },
  members: {
  },
} as const;

const favoriteCarImages = Object.fromEntries(
  Object.entries(import.meta.glob("./*.webp", { eager: true, import: "default" })).map(([assetPath, assetUrl]) => {
    const fileName = assetPath.split("/").pop()?.replace(/\.webp$/i, "") ?? "";
    const assetKey = fileName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    return [assetKey, assetUrl as string];
  }),
) as Record<string, string>;

const getFavoriteCarImage = (value: string) => {
  const assetKey = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return favoriteCarImages[assetKey] || "";
};

const RACES_SECTION_BG_IMAGE = IMAGE_ASSETS.sectionBackgrounds.results.landscape;
const ASSETTO_SECTION_BG_IMAGE = "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=2200&q=80";
const TEAM_CATEGORY_BG_IMAGES: Record<MemberCategory, string> = {
  management: managementBackgroundImage,
  lmu: lmuBackgroundImage,
  assetto: ASSETTO_SECTION_BG_IMAGE,
};
const TEAM_CATEGORY_ORDER: MemberCategory[] = ["management", "lmu", "assetto"];
const getMemberImage = (member: Pick<Member, "image">) => member.image || avatarFallbackImage;
const getMemberSocialLinks = (member: Pick<Member, "social1Label" | "social1Href" | "social2Label" | "social2Href">) =>
  [
    { label: member.social1Label, href: member.social1Href },
    { label: member.social2Label, href: member.social2Href },
  ].filter((social) => social.label.trim() && social.href.trim());

const getSectionBackgroundStyle = (section: SectionId, isPortrait: boolean) => ({
  backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.58) 45%, rgba(220,0,0,0.24) 100%), url(${isPortrait ? IMAGE_ASSETS.sectionBackgrounds[section].portrait : IMAGE_ASSETS.sectionBackgrounds[section].landscape})`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: section === "home" ? "center 3%" : "center center",
});

const navigation: Array<{ key: SectionId; label: LocalizedText }> = [
  { key: "about", label: localizedText("O nas", "About Us", "关于我们") },
  { key: "partners", label: localizedText("Partnerzy", "Partners", "合作伙伴") },
  { key: "team", label: localizedText("Zespół", "Team", "车队") },
  { key: "achievements", label: localizedText("Osiągnięcia", "Achievements", "成就") },
  { key: "contact", label: localizedText("Kontakt", "Contact", "联系") },
];

const languageOptions: Array<{ code: Language; label: string; flagSrc: string }> = [
  { code: "en", label: "EN", flagSrc: IMAGE_ASSETS.flags.en },
  { code: "pl", label: "PL", flagSrc: IMAGE_ASSETS.flags.pl },
  { code: "zh", label: "中文", flagSrc: IMAGE_ASSETS.flags.zh },
];

const dictionary = {
  pl: {
    tagline: "Simracing Team",
    heroTitleTop: "GLS PXN Racing",
    heroTitleBottom: "Simracing bez kompromisów",
    heroText:
      "Profesjonalny polski zespół e-sportowy rywalizujący na najwyższym poziomie w Assetto Corsa i Le Mans Ultimate. Łączymy pasję do wyścigów z nowoczesną technologią PXN.",
    sectionHint: "Przewiń w dół, aby poznać nasz zespół, partnerów i najnowsze sukcesy na torze.",
    dashboardBadge: "Garage Dashboard",
    latestResult: "Najnowszy wynik",
    noRaceData: "Brak nadchodzących wyścigów",
    partnersEyebrow: "Partnerzy",
    partnersTitle: "Technologia i wsparcie",
    partnersText: "Współpracujemy z markami, które wzmacniają nas na torze i poza nim.",
    partnerPlaceholder: "Partner zespołu",
    aboutEyebrow: "O nas",
    aboutTitle: "Rywalizacja, organizacja i profesjonalizm",
    aboutText:
      "GLS PXN Racing buduje swoją pozycję w simracingu od lipca 2023 roku. Koncentrujemy się na wyścigach endurance i sprintach, rozwoju kierowców oraz budowie silnej, dobrze zorganizowanej drużyny.",
    aboutCards: [
      {
        title: "Wyścigi i trening",
        text: "Regularna praca, analiza tempa i przygotowanie do najważniejszych startów w kalendarzu.",
      },
      {
        title: "Struktura zespołu",
        text: "Czytelny podział na management, LMU i Assetto Corsa pozwala nam skutecznie rozwijać każdy projekt.",
      },
      {
        title: "Media i sponsorzy",
        text: "Budujemy zasięg dzięki transmisjom, social mediom i stałej ekspozycji naszych partnerów.",
      },
    ],
    teamEyebrow: "Team",
    teamTitle: "Ludzie GLS PXN Racing",
    teamText: "Poznaj kierowców i management odpowiedzialnych za rozwój oraz wyniki zespołu.",
    categories: {
      management: {
        eyebrow: "Zarząd",
        title: "Management",
        description: "Odpowiada za kierunek rozwoju zespołu, partnerstwa i codzienną organizację.",
      },
      lmu: {
        eyebrow: "Le Mans Ultimate",
        title: "Dywizja LMU",
        description: "Skład skupiony na Le Mans Ultimate i rywalizacji długodystansowej.",
      },
      assetto: {
        eyebrow: "Assetto Corsa",
        title: "Dywizja AC",
        description: "Kierowcy startujący w czołowych ligach Assetto Corsa i wybranych eventach specjalnych.",
      },
    },
    favoriteTrack: "Ulubiony tor",
    favoriteCar: "Ulubione auto",
    racingNumber: "Numer startowy",
    teamSince: "W zespole od",
    openProfile: "Otwórz profil",
    profileTitle: "Profil członka zespołu",
    nickname: "Pseudonim",
    strengths: "Mocne strony",
    noMembers: "Brak danych o członkach zespołu.",
    resultsEyebrow: "Wyścigi",
    resultsTitle: "Harmonogram i wyniki",
    resultsText: "Śledź nadchodzące starty i sprawdzaj wyniki poprzednich rund.",
    noResults: "Brak wyników do wyświetlenia.",
    achievementsEyebrow: "Osiągnięcia",
    achievementsTitle: "Nasze sukcesy",
    achievementsText: "Najważniejsze wyniki GLS PXN Racing, od sprintów po wielogodzinne wyścigi endurance.",
    noAchievements: "Brak osiągnięć do wyświetlenia.",
    contactEyebrow: "Kontakt",
    contactTitle: "Dołącz do nas lub napisz",
    contactText:
      "Chcesz współpracować, dołączyć do składu albo zostać sponsorem? Skontaktuj się z nami przez email lub social media.",
    driverFormDiscordLabel: "Discord ID",
    driverFormPlatformLabel: "Platforma",
    driverFormBioLabel: "O sobie i osiągnięcia",
    driverFormSubmit: "Wyślij zgłoszenie",
    driverFormSending: "Wysyłanie...",
    driverFormSuccess: "Zgłoszenie wysłane.",
    driverFormError: "Nie udało się wysłać. Spróbuj ponownie.",
    driverFormMissingEndpoint: "Brak konfiguracji formularza. Skontaktuj się przez email.",
    contactSegments: [
      {
        title: "Dla kierowców",
        kind: "drivers",
        text: "Chcesz dołączyć do składu? Wyślij krótki opis, doświadczenie i dostępność startów.",
        ctaLabel: "Zgłoś się",
        ctaHref: "mailto:followgls@outlook.com",
      },
      {
        title: "Dla sponsorów",
        kind: "sponsors",
        text: "Szukasz partnerstwa? Przygotujemy ofertę ekspozycji i aktywacji marki.",
        ctaLabel: "Zostań sponsorem",
        ctaHref: "mailto:followgls@outlook.com",
      },
    ],
    contactLinks: [
      { label: "Email", href: "mailto:followgls@outlook.com" },
      { label: "YouTube", href: "https://www.youtube.com/@followGLS" },
      { label: "Instagram", href: "https://instagram.com/followgls" },
      { label: "X", href: "https://x.com/followgls" },
      { label: "Facebook", href: "https://facebook.com/glsracing" },
      { label: "TikTok", href: "https://www.tiktok.com/@followgls" },
    ],
    close: "Zamknij",
    langLabel: "Język",
    footerNote: "GLS PXN Racing 2026. Wszelkie prawa zastrzeżone.",
  },
  en: {
    tagline: "Simracing Team",
    heroTitleTop: "GLS PXN Racing",
    heroTitleBottom: "Simracing without compromise",
    heroText:
      "A professional Polish e-sports team competing at the highest level in Assetto Corsa and Le Mans Ultimate. We combine a passion for racing with cutting-edge PXN technology.",
    sectionHint: "Scroll down to meet our team, discover our partners, and review our latest track achievements.",
    dashboardBadge: "Garage Dashboard",
    latestResult: "Latest result",
    noRaceData: "No upcoming races",
    partnersEyebrow: "Partners",
    partnersTitle: "Technology and support",
    partnersText: "We work with brands that strengthen us on track and beyond it.",
    partnerPlaceholder: "Team partner",
    aboutEyebrow: "About Us",
    aboutTitle: "Competition, organization and professionalism",
    aboutText:
      "GLS PXN Racing has been building its simracing presence since July 2023. We focus on endurance and sprint racing, driver development, and a strong, organized team structure.",
    aboutCards: [
      {
        title: "Racing and training",
        text: "Consistent preparation, pace analysis, and focused work before every major race weekend.",
      },
      {
        title: "Team structure",
        text: "Clear divisions across management, LMU, and Assetto Corsa keep every project organized and competitive.",
      },
      {
        title: "Media and sponsors",
        text: "We grow our reach through live broadcasts, social media activity, and strong partner visibility.",
      },
    ],
    teamEyebrow: "Team",
    teamTitle: "The people behind GLS PXN Racing",
    teamText: "Meet the drivers and management working together to grow the team and deliver results.",
    categories: {
      management: {
        eyebrow: "Management",
        title: "Management",
        description: "Responsible for team direction, partner relationships, and daily operations.",
      },
      lmu: {
        eyebrow: "Le Mans Ultimate",
        title: "LMU Division",
        description: "A roster focused on Le Mans Ultimate and endurance competition.",
      },
      assetto: {
        eyebrow: "Assetto Corsa",
        title: "AC Division",
        description: "Drivers competing in top Assetto Corsa leagues and selected special events.",
      },
    },
    favoriteTrack: "Favorite track",
    favoriteCar: "Favorite car",
    racingNumber: "Racing number",
    teamSince: "Team since",
    openProfile: "Open profile",
    profileTitle: "Team member profile",
    nickname: "Nickname",
    strengths: "Strengths",
    noMembers: "No team members found.",
    resultsEyebrow: "Races",
    resultsTitle: "Schedule and results",
    resultsText: "Follow upcoming starts and review our latest results.",
    noResults: "No results to display.",
    achievementsEyebrow: "Achievements",
    achievementsTitle: "Our successes",
    achievementsText: "The key GLS PXN Racing results, from sprint victories to endurance podium finishes.",
    noAchievements: "No achievements to display.",
    contactEyebrow: "Contact",
    contactTitle: "Join us or get in touch",
    contactText:
      "Want to work with us, join the roster, or become a sponsor? Reach out via email or social media.",
    driverFormDiscordLabel: "Discord ID",
    driverFormPlatformLabel: "Platform AC/ACC/Iracing/Other?",
    driverFormBioLabel: "About you and achievements",
    driverFormSubmit: "Send application",
    driverFormSending: "Sending...",
    driverFormSuccess: "Application sent.",
    driverFormError: "Could not send. Please try again.",
    driverFormMissingEndpoint: "Form not configured. Please contact us by email.",
    contactSegments: [
      {
        title: "For drivers",
        kind: "drivers",
        text: "Want to join the roster? Send a short intro, your experience, and availability.",
        ctaLabel: "Apply to race",
        ctaHref: "mailto:followgls@outlook.com",
      },
      {
        title: "For sponsors",
        kind: "sponsors",
        text: "Looking for a partnership? We can prepare an exposure and activation plan.",
        ctaLabel: "Become a sponsor",
        ctaHref: "mailto:followgls@outlook.com",
      },
    ],
    contactLinks: [
      { label: "Email", href: "mailto:followgls@outlook.com" },
      { label: "YouTube", href: "https://www.youtube.com/@followGLS" },
      { label: "Instagram", href: "https://instagram.com/followgls" },
      { label: "X", href: "https://x.com/followgls" },
      { label: "Facebook", href: "https://facebook.com/glsracing" },
      { label: "TikTok", href: "https://www.tiktok.com/@followgls" },
    ],
    close: "Close",
    langLabel: "Language",
    footerNote: "GLS PXN Racing 2026. All rights reserved.",
  },
  zh: {
    tagline: "Simracing Team",
    heroTitleTop: "GLS PXN Racing",
    heroTitleBottom: "毫不妥协的模拟赛车",
    heroText:
      "我们是一支来自波兰的职业电竞车队，活跃于 Assetto Corsa 和 Le Mans Ultimate 的高水平赛事。我们将赛车热情与先进的 PXN 技术结合在一起。",
    sectionHint: "向下滚动，了解我们的车手、合作伙伴以及最新赛道成绩。",
    dashboardBadge: "Garage Dashboard",
    latestResult: "最新成绩",
    noRaceData: "暂无即将开始的比赛",
    partnersEyebrow: "合作伙伴",
    partnersTitle: "技术与支持",
    partnersText: "我们与能够在赛道内外为车队提供支持的品牌合作。",
    partnerPlaceholder: "车队合作伙伴",
    aboutEyebrow: "关于我们",
    aboutTitle: "竞争、组织与专业精神",
    aboutText:
      "GLS PXN Racing 自 2023 年 7 月起持续打造自己的 simracing 品牌。我们专注于耐力赛、冲刺赛、车手培养以及高效的团队结构。",
    aboutCards: [
      {
        title: "比赛与训练",
        text: "稳定训练、节奏分析，以及每场重要比赛前的充分准备。",
      },
      {
        title: "团队结构",
        text: "管理层、LMU 和 Assetto Corsa 分工清晰，让每个项目都能高效推进。",
      },
      {
        title: "媒体与赞助",
        text: "我们通过直播、社交媒体和合作伙伴曝光持续扩大影响力。",
      },
    ],
    teamEyebrow: "车队",
    teamTitle: "GLS PXN Racing 成员",
    teamText: "认识为车队成长与成绩共同努力的车手和管理成员。",
    categories: {
      management: {
        eyebrow: "管理层",
        title: "Management",
        description: "负责车队方向、合作关系和日常运营。",
      },
      lmu: {
        eyebrow: "Le Mans Ultimate",
        title: "LMU Division",
        description: "专注于 Le Mans Ultimate 和耐力赛事的阵容。",
      },
      assetto: {
        eyebrow: "Assetto Corsa",
        title: "AC Division",
        description: "参加顶级 Assetto Corsa 联赛和精选特别赛事的车手阵容。",
      },
    },
    favoriteTrack: "最喜欢的赛道",
    favoriteCar: "最喜欢的赛车",
    racingNumber: "车号",
    teamSince: "加入时间",
    openProfile: "打开资料",
    profileTitle: "成员资料",
    nickname: "昵称",
    strengths: "优势",
    noMembers: "暂无成员数据。",
    resultsEyebrow: "比赛",
    resultsTitle: "赛程与成绩",
    resultsText: "查看即将到来的比赛并回顾最近的成绩。",
    noResults: "暂无结果可显示。",
    achievementsEyebrow: "成就",
    achievementsTitle: "我们的成绩",
    achievementsText: "GLS PXN Racing 的关键成绩总览，从冲刺赛胜利到耐力赛领奖台。",
    noAchievements: "暂无成就可显示。",
    contactEyebrow: "联系",
    contactTitle: "加入我们或联系我们",
    contactText:
      "想合作、加入车队或成为赞助商？欢迎通过电子邮件或社交媒体联系我们。",
    driverFormDiscordLabel: "Discord ID",
    driverFormPlatformLabel: "平台",
    driverFormBioLabel: "自我介绍与成绩",
    driverFormSubmit: "提交申请",
    driverFormSending: "正在发送...",
    driverFormSuccess: "已发送申请。",
    driverFormError: "发送失败，请重试。",
    driverFormMissingEndpoint: "表单未配置，请通过邮件联系。",
    contactSegments: [
      {
        title: "车手招募",
        kind: "drivers",
        text: "想加入车队？请发送简要介绍、经验与可参赛时间。",
        ctaLabel: "申请加入",
        ctaHref: "mailto:followgls@outlook.com",
      },
      {
        title: "赞助合作",
        kind: "sponsors",
        text: "寻求合作伙伴？我们可以提供品牌曝光与活动方案。",
        ctaLabel: "成为赞助商",
        ctaHref: "mailto:followgls@outlook.com",
      },
    ],
    contactLinks: [
      { label: "Email", href: "mailto:followgls@outlook.com" },
      { label: "YouTube", href: "https://www.youtube.com/@followGLS" },
      { label: "Instagram", href: "https://instagram.com/followgls" },
      { label: "X", href: "https://x.com/followgls" },
      { label: "Facebook", href: "https://facebook.com/glsracing" },
      { label: "TikTok", href: "https://www.tiktok.com/@followgls" },
    ],
    close: "关闭",
    langLabel: "语言",
    footerNote: "GLS PXN Racing 2026。保留所有权利。",
  },
} as const;

const partners: Partner[] = [
  {
    name: "PXN",
    website: "https://pxn-game.com?sca_ref=9384093.tHvfRbPqFn",
    image: IMAGE_ASSETS.logos.partnerPxn,
    description: localizedText(
      "Sponsor tytularny dostarczający sprzęt i technologię dla nowoczesnego simracingu.",
      "Title sponsor delivering hardware and technology for modern simracing.",
      "冠名赞助商，为现代模拟赛车提供设备与技术支持。",
    ),
    promoLabel: localizedText("Kod promocyjny", "Promo code", "优惠码"),
    promoCode: "GLSRACING",
    promoDiscount: "5%",
  },
];

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/^\ufeff/, "").replace(/\s+/g, "_");

const getValue = (row: CsvRow, keys: string[]) => {
  for (const key of keys) {
    const normalizedKey = normalizeHeader(key);
    const value = row[normalizedKey]?.trim();
    if (value) {
      return value;
    }
  }

  return "";
};

const splitCsvList = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const parseBoolean = (value: string, fallback = true) => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;
  if (["1", "true", "yes", "y", "active", "current"].includes(normalized)) return true;
  if (["0", "false", "no", "n", "inactive", "former", "archived"].includes(normalized)) return false;
  return fallback;
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const isLiveCsvUrl = (url: string) => Boolean(url) && !url.includes("TUTAJ_WKLEJE_LINK");

function parseCsv(csvText: string): CsvRow[] {
  const rows: string[][] = [];
  let currentCell = "";
  let currentRow: string[] = [];
  let insideQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const character = csvText[index];
    const nextCharacter = csvText[index + 1];

    if (character === '"') {
      if (insideQuotes && nextCharacter === '"') {
        currentCell += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
      continue;
    }

    if (character === "," && !insideQuotes) {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if ((character === "\n" || character === "\r") && !insideQuotes) {
      if (character === "\r" && nextCharacter === "\n") {
        index += 1;
      }

      currentRow.push(currentCell);
      if (currentRow.some((cell) => cell.trim() !== "")) {
        rows.push(currentRow);
      }
      currentCell = "";
      currentRow = [];
      continue;
    }

    currentCell += character;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    if (currentRow.some((cell) => cell.trim() !== "")) {
      rows.push(currentRow);
    }
  }

  const [headerRow, ...dataRows] = rows;
  if (!headerRow) {
    return [];
  }

  const headers = headerRow.map((header, index) => normalizeHeader(index === 0 ? header.replace(/^\ufeff/, "") : header));

  return dataRows.map((row) => {
    const item: CsvRow = {};
    headers.forEach((header, index) => {
      item[header] = (row[index] ?? "").trim();
    });
    return item;
  });
}

const normalizeCategory = (value: string): MemberCategory => {
  const normalized = value.toLowerCase();

  if (normalized.includes("manage") || normalized.includes("manager") || normalized.includes("admin")) {
    return "management";
  }

  if (normalized.includes("assetto") || normalized.includes("ac")) {
    return "assetto";
  }

  return "lmu";
};

const mapMemberRow = (row: CsvRow, index: number): Member => {
  const name = getValue(row, ["name", "full_name", "fullname", "member_name"]);
  const sharedRole = getValue(row, ["role"]);
  const sharedBio = getValue(row, ["bio", "description"]);
  const sharedHighlights = getValue(row, ["highlights", "skills", "strengths"]);
  const memberStatus = getValue(row, ["is_active", "active", "status", "member_status", "current_member"]);

  return {
    id: getValue(row, ["id", "slug"]) || slugify(name) || `member-${index + 1}`,
    name: name || `Member ${index + 1}`,
    nickname: getValue(row, ["nickname", "nick", "tag"]),
    category: normalizeCategory(getValue(row, ["category", "division", "group"])),
    isActive: parseBoolean(memberStatus, true),
    role: {
      pl: getValue(row, ["role_pl", "rolepl", "pl_role"]) || sharedRole,
      en: getValue(row, ["role_en", "roleen", "en_role"]) || sharedRole,
      zh: getValue(row, ["role_zh", "role_cn", "rolezh", "zh_role", "cn_role"]) || sharedRole,
    },
    location: getValue(row, ["location", "country", "city"]),
    image: getValue(row, ["image", "photo", "avatar", "image_url", "photo_url"]) || avatarFallbackImage,
    bio: {
      pl: getValue(row, ["bio_pl", "biopl", "pl_bio"]) || sharedBio,
      en: getValue(row, ["bio_en", "bioen", "en_bio"]) || sharedBio,
      zh: getValue(row, ["bio_zh", "bio_cn", "biozh", "zh_bio", "cn_bio"]) || sharedBio,
    },
    highlights: {
      pl: splitCsvList(getValue(row, ["highlights_pl", "highlightspl", "pl_highlights"]) || sharedHighlights),
      en: splitCsvList(getValue(row, ["highlights_en", "highlightsen", "en_highlights"]) || sharedHighlights),
      zh: splitCsvList(getValue(row, ["highlights_zh", "highlights_cn", "highlightszh", "zh_highlights", "cn_highlights"]) || sharedHighlights),
    },
    favoriteTrack: getValue(row, ["favorite_track", "favourite_track", "track", "ulubiony_tor"]),
    favoriteCar: getValue(row, ["favorite_car", "favourite_car", "car", "ulubione_auto"]),
    racingNumber: getValue(row, ["racing_number", "number", "driver_number", "numer_startowy"]),
    teamSince: getValue(row, ["team_since", "joined_team", "join_date", "date_joined", "w_zespole_od"]),
    social1Label:
      getValue(row, ["primary_social_label", "primarysociallabel", "social1label", "social_1_label", "social1_label", "social_label_1"]),
    social1Href:
      getValue(row, ["primary_social_href", "primarysocialhref", "social1href", "social_1_href", "social1_href", "social_link_1"]),
    social2Label:
      getValue(row, ["secondary_social_label", "secondarysociallabel", "social2label", "social_2_label", "social2_label", "social_label_2"]),
    social2Href:
      getValue(row, ["secondary_social_href", "secondarysocialhref", "social2href", "social_2_href", "social2_href", "social_link_2"]),
  };
};
const mapRaceRow = (row: CsvRow, index: number): Race => {
  const raceTitle = getValue(row, ["race_title", "racetitle", "title", "race", "event", "event_title", "name"]);

  return {
    id: getValue(row, ["id", "slug"]) || slugify(raceTitle) || `race-${index + 1}`,
    raceTitle: raceTitle || `Race ${index + 1}`,
    date: getValue(row, ["date", "race_date", "event_date"]),
    leagueName: getValue(row, ["league_name", "leaguename", "league", "series", "championship"]),
    carClass: getValue(row, ["car_class", "carclass", "class", "category"]),
    streamPlatform: getValue(row, ["stream_platform", "platform", "stream", "broadcast_platform"]),
    streamUrl: getValue(row, ["stream_url", "stream_link", "broadcast_url", "watch_url", "link"]),
    eventType: getValue(row, ["event_type", "type", "race_type", "event_category"]),
    participants: getValue(row, ["participants", "drivers", "lineup", "driver_lineup", "entry"]),
    resultBy: getValue(row, ["result_by", "earned_by", "winner", "winners", "driver_result", "scored_by"]),
    position: getValue(row, ["position", "result", "finish", "last_result"]),
  };
};

const mapAchievementRow = (row: CsvRow, index: number): Achievement => {
  const eventName = getValue(row, ["event_name", "event", "achievement_title", "title", "name"]);

  return {
    id: getValue(row, ["id", "slug"]) || slugify(eventName) || `achievement-${index + 1}`,
    eventName: eventName || `Achievement ${index + 1}`,
    leagueName: getValue(row, ["league_name", "league", "series", "championship"]),
    position: getValue(row, ["position", "place", "result"]) || "P1",
    winners: getValue(row, ["winners", "lineup", "drivers", "driver", "earned_by"]),
    category: getValue(row, ["category", "type", "achievement_type"]) || "Team",
    game: getValue(row, ["game", "sim", "title_game"]),
    car: getValue(row, ["car", "car_name", "vehicle", "vehicle_name", "auto"]),
    background: getValue(row, ["background", "background_url", "bg", "image", "image_url"]),
    date: getValue(row, ["date", "event_date", "achievement_date"]),
  };
};

const parseRaceDate = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(`${trimmed}T00:00:00`);
  }

  const dotMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    const [, day, month, year] = dotMatch;
    return new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`);
  }

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00`);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};


type RaceWithTimestamp = Race & { timestamp: number | null };

const withRaceTimestamp = (race: Race): RaceWithTimestamp => ({
  ...race,
  timestamp: parseRaceDate(race.date)?.getTime() ?? null,
});

const getRacePositionRank = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const numberMatch = trimmed.match(/\d+/);
  return numberMatch ? Number(numberMatch[0]) : null;
};

const getRacePositionClass = (_value: string) => "text-white font-black";

const getRacePositionBadgeClass = (value: string) => {
  const rank = getRacePositionRank(value);

  if (rank === 1) {
    return "border-yellow-400/35 bg-[linear-gradient(135deg,rgba(250,204,21,0.34),rgba(255,255,255,0.08))] shadow-[0_18px_50px_-30px_rgba(250,204,21,0.55)]";
  }

  if (rank === 2) {
    return "border-slate-300/35 bg-[linear-gradient(135deg,rgba(226,232,240,0.28),rgba(255,255,255,0.07))] shadow-[0_18px_50px_-30px_rgba(226,232,240,0.42)]";
  }

  if (rank === 3) {
    return "border-orange-500/35 bg-[linear-gradient(135deg,rgba(194,120,50,0.32),rgba(255,255,255,0.07))] shadow-[0_18px_50px_-30px_rgba(194,120,50,0.48)]";
  }

  return "border-[#dc0000]/25 bg-[linear-gradient(135deg,rgba(220,0,0,0.18),rgba(255,255,255,0.04))] shadow-[0_18px_50px_-30px_rgba(220,0,0,0.45)]";
};

const getRacePositionPanelClass = (value: string) => {
  const rank = getRacePositionRank(value);

  if (rank === 1) {
    return "border-yellow-400/35 bg-[linear-gradient(135deg,rgba(250,204,21,0.22),rgba(255,255,255,0.05))]";
  }

  if (rank === 2) {
    return "border-slate-300/35 bg-[linear-gradient(135deg,rgba(226,232,240,0.16),rgba(255,255,255,0.05))]";
  }

  if (rank === 3) {
    return "border-orange-500/35 bg-[linear-gradient(135deg,rgba(194,120,50,0.2),rgba(255,255,255,0.05))]";
  }

  return "border-[#dc0000]/25 bg-[#dc0000]/10";
};

const getRaceEventCardClass = (value: string) => {
  const rank = getRacePositionRank(value);

  if (rank === 1) {
    return "border-yellow-400/25 bg-[linear-gradient(145deg,rgba(250,204,21,0.14),rgba(255,255,255,0.04),rgba(0,0,0,0.32))] shadow-[0_24px_70px_-34px_rgba(250,204,21,0.34)]";
  }

  if (rank === 2) {
    return "border-slate-300/25 bg-[linear-gradient(145deg,rgba(226,232,240,0.12),rgba(255,255,255,0.04),rgba(0,0,0,0.32))] shadow-[0_24px_70px_-34px_rgba(226,232,240,0.26)]";
  }

  if (rank === 3) {
    return "border-orange-500/25 bg-[linear-gradient(145deg,rgba(194,120,50,0.14),rgba(255,255,255,0.04),rgba(0,0,0,0.32))] shadow-[0_24px_70px_-34px_rgba(194,120,50,0.28)]";
  }

  return "border-[#dc0000]/25 bg-[linear-gradient(145deg,rgba(220,0,0,0.16),rgba(255,255,255,0.04),rgba(0,0,0,0.32))] shadow-[0_24px_70px_-34px_rgba(220,0,0,0.28)]";
};

const formatRacePosition = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "No result";
  }

  const numberMatch = trimmed.match(/\d+/);
  if (!numberMatch) {
    return trimmed;
  }

  return `${numberMatch[0]}st`;
};

const formatRaceMeta = (value: string) => value.trim() || "-";

const splitLineup = (value: string) =>
  value
    .split(/[\n,;/|]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeNameForLookup = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .toLowerCase();

const getLookupNameVariants = (value: string) => {
  const trimmed = value.trim();
  const variants = new Set<string>();
  const pushVariant = (candidate: string) => {
    const normalized = normalizeNameForLookup(candidate);
    if (normalized) {
      variants.add(normalized);
    }
  };

  pushVariant(trimmed);
  pushVariant(trimmed.replace(/\([^)]*\)/g, " "));
  pushVariant(trimmed.replace(/\[[^\]]*\]/g, " "));
  pushVariant(trimmed.replace(/\s+-\s+.*$/, ""));
  pushVariant(trimmed.replace(/\s+[|/]\s+.*$/, ""));
  pushVariant(trimmed.replace(/\s+#\d+.*$/, ""));
  return [...variants];
};


const getAchievementMedal = (rank: number) => {
  if (rank === 1) return "\uD83E\uDD47";
  if (rank === 2) return "\uD83E\uDD48";
  if (rank === 3) return "\uD83E\uDD49";
  return "\uD83C\uDFC1";
};

const getAchievementMedalClass = (rank: number) => {
  if (rank === 1) {
    return "border-yellow-400/35 bg-yellow-400/12 text-yellow-200";
  }

  if (rank === 2) {
    return "border-slate-300/35 bg-slate-300/10 text-slate-100";
  }

  if (rank === 3) {
    return "border-orange-400/35 bg-orange-400/12 text-orange-200";
  }

  return "border-white/15 bg-white/5 text-zinc-200";
};

const getAchievementHeadline = (achievement: Pick<Achievement, "eventName" | "leagueName" | "category" | "game">) =>
  achievement.eventName || achievement.leagueName || achievement.category || achievement.game || "GLS PXN Racing";
const RaceCountdown = ({ targetDate }: CountdownProps) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!targetDate) {
      return;
    }

    const timerId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [targetDate]);

  if (!targetDate) {
    return "00d 00h 00m 00s";
  }

  const remainingMs = Math.max(0, targetDate.getTime() - now);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(days).padStart(2, "0")}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
};
const renderStreamIcon = (platform: string) => {
  switch (platform) {
    case "twitch":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
          <path d="M4 3h16v11l-4 4h-4l-2 2H7v-2H4V3zm2 2v9h3v3l3-3h3l3-3V5H6zm5 2h2v4h-2V7zm4 0h2v4h-2V7z" />
        </svg>
      );
    case "kick":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
          <path d="M4 4h5v6l5-6h6l-7 8 7 8h-6l-5-6v6H4V4z" />
        </svg>
      );
    case "youtube":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
          <path d="M23 12s0-3.1-.4-4.6a3 3 0 0 0-2.1-2.1C19 5 12 5 12 5s-7 0-8.5.4a3 3 0 0 0-2.1 2.1C1 8.9 1 12 1 12s0 3.1.4 4.6a3 3 0 0 0 2.1 2.1C5 19 12 19 12 19s7 0 8.5-.4a3 3 0 0 0 2.1-2.1C23 15.1 23 12 23 12zm-14 3V9l5.5 3L9 15z" />
        </svg>
      );
    default:
      return null;
  }
};
const getStreamButtonClass = (platform: string) => {
  switch (platform) {
    case "twitch":
      return "border-[#9147ff]/40 bg-[#9147ff]/18 text-[#f1e7ff] hover:bg-[#9147ff]/28";
    case "kick":
      return "border-[#53fc18]/35 bg-[#53fc18]/14 text-[#e9ffd9] hover:bg-[#53fc18]/24";
    case "youtube":
      return "border-[#ff3d3d]/40 bg-[#ff3d3d]/18 text-[#fff1f1] hover:bg-[#ff3d3d]/28";
    default:
      return "border-white/15 bg-white/10 text-white hover:bg-white/15";
  }
};
const localizedLanguageOrder: Record<Language, Language[]> = {
  pl: ["pl", "en", "zh"],
  en: ["en", "pl", "zh"],
  zh: ["zh", "en", "pl"],
};

const getLocalizedText = (value: LocalizedText, language: Language) => {
  for (const lang of localizedLanguageOrder[language]) {
    if (value[lang]) {
      return value[lang];
    }
  }

  return "";
};

const getLocalizedList = (value: LocalizedList, language: Language) => {
  for (const lang of localizedLanguageOrder[language]) {
    if (value[lang].length > 0) {
      return value[lang];
    }
  }

  return [];
};

const getMemberRole = (member: Member, language: Language) => getLocalizedText(member.role, language);

const getMemberBio = (member: Member, language: Language) => getLocalizedText(member.bio, language);

const formatMemberStat = (value: string) => value.trim() || "-";

const formatRacingNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

const getMemberHighlights = (member: Member, language: Language) => getLocalizedList(member.highlights, language);

const fetchCsvText = async (url: string) => {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.text();
};

const fetchRaces = async () => {
  const racesCsvText = await fetchCsvText(RACES_SHEET_URL);
  const parsedRaces = parseCsv(racesCsvText)
    .map(mapRaceRow)
    .filter((race) => race.raceTitle);

  return parsedRaces
    .map((race) => ({ race, timestamp: parseRaceDate(race.date)?.getTime() ?? 0 }))
    .sort((a, b) => b.timestamp - a.timestamp)
    .map(({ race }) => race);
};

const fetchAchievements = async () => {
  const achievementsCsvText = await fetchCsvText(ACHIEVEMENTS_SHEET_URL);
  return parseCsv(achievementsCsvText)
    .map(mapAchievementRow)
    .filter((achievement) => achievement.eventName);
};

export function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [activeSection, setActiveSection] = useState<SectionId>("home");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const homeSnapLockedRef = useRef(false);
  const [visibleSections, setVisibleSections] = useState<Record<SectionId, boolean>>({
    home: true,
    partners: false,
    about: false,
    team: false,
    achievements: false,
    contact: false,
  });
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [races, setRaces] = useState<Race[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [isPortrait, setIsPortrait] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(orientation: portrait)").matches : false));
  const [isDataReady, setIsDataReady] = useState(false);
  const [copiedPromo, setCopiedPromo] = useState<{ code: string; x: number; y: number } | null>(null);
  const promoCopyTimeoutRef = useRef<number | null>(null);

  const [driverForm, setDriverForm] = useState({ discordId: "", platform: "", bio: "" });
  const [driverFormStatus, setDriverFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [driverFormMessage, setDriverFormMessage] = useState("");

  const t = dictionary[language];
  const currentLanguageOption = languageOptions.find((option) => option.code === language) ?? languageOptions[0];
  const liveMembersEnabled = isLiveCsvUrl(MEMBERS_SHEET_URL);
  const liveRacesEnabled = isLiveCsvUrl(RACES_SHEET_URL);
  const liveAchievementsEnabled = isLiveCsvUrl(ACHIEVEMENTS_SHEET_URL);
  const promoCopiedLabel = language === "pl" ? "Skopiowano do schowka" : language === "zh" ? "已复制到剪贴板" : "Copied to clipboard";

  const handlePromoCopy = async (promoCode: string, position?: { x: number; y: number }) => {
    if (!promoCode) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(promoCode);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = promoCode;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
    } catch {
      return;
    }
    setCopiedPromo({
      code: promoCode,
      x: position?.x ?? 0,
      y: position?.y ?? 0,
    });
    if (promoCopyTimeoutRef.current) {
      window.clearTimeout(promoCopyTimeoutRef.current);
    }
    promoCopyTimeoutRef.current = window.setTimeout(() => setCopiedPromo(null), 1500);
  };

  const resetDriverFormStatus = () => {
    if (driverFormStatus !== "idle") {
      setDriverFormStatus("idle");
      setDriverFormMessage("");
    }
  };

  const updateDriverFormField = (field: "discordId" | "platform" | "bio") => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setDriverForm((prev) => ({ ...prev, [field]: value }));
    resetDriverFormStatus();
  };

  const handleDriverFormSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (!driverForm.discordId.trim() || !driverForm.platform.trim() || !driverForm.bio.trim()) {
      setDriverFormStatus("error");
      setDriverFormMessage(t.driverFormError);
      return;
    }
    if (!DRIVER_APPLICATION_ENDPOINT || DRIVER_APPLICATION_ENDPOINT.includes("PASTE")) {
      setDriverFormStatus("error");
      setDriverFormMessage(t.driverFormMissingEndpoint);
      return;
    }
    setDriverFormStatus("submitting");
    setDriverFormMessage("");
    try {
      const response = await fetch(DRIVER_APPLICATION_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discordId: driverForm.discordId.trim(),
          platform: driverForm.platform.trim(),
          bio: driverForm.bio.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error("Request failed with status " + response.status);
      }
      const result = await response.json().catch(() => ({ ok: true }));
      if (result?.ok === false) {
        throw new Error(result?.error || "Request failed");
      }
      setDriverFormStatus("success");
      setDriverFormMessage(t.driverFormSuccess);
      setDriverForm({ discordId: "", platform: "", bio: "" });
    } catch {
      setDriverFormStatus("error");
      setDriverFormMessage(t.driverFormError);
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const media = window.matchMedia("(orientation: portrait)");
    const onChange = () => setIsPortrait(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsDataReady(false);
      if (!liveMembersEnabled && !liveRacesEnabled && !liveAchievementsEnabled) {
        if (!isMounted) return;
        setMembers(fallbackMembers);
        setRaces(fallbackRaces);
        setAchievements(fallbackAchievements);
        setIsDataReady(true);
        return;
      }

      try {
        const [membersCsvText, parsedRaces, parsedAchievements] = await Promise.all([
          liveMembersEnabled ? fetchCsvText(MEMBERS_SHEET_URL) : Promise.resolve(""),
          liveRacesEnabled ? fetchRaces() : Promise.resolve([] as Race[]),
          liveAchievementsEnabled ? fetchAchievements() : Promise.resolve([] as Achievement[]),
        ]);

        const parsedMembers = liveMembersEnabled
          ? parseCsv(membersCsvText).map(mapMemberRow).filter((member) => member.name)
          : fallbackMembers;

        if (!isMounted) return;

        setMembers(parsedMembers.length ? parsedMembers : fallbackMembers);
        setRaces(parsedRaces.length ? parsedRaces : fallbackRaces);
        setAchievements(parsedAchievements.length ? parsedAchievements : fallbackAchievements);
        setIsDataReady(true);
      } catch {
        if (!isMounted) return;
        setMembers(fallbackMembers);
        setRaces(fallbackRaces);
        setAchievements(fallbackAchievements);
        setIsDataReady(true);
      }
    };

    void loadData();

    return () => {
      isMounted = false;
    };
  }, [liveMembersEnabled, liveRacesEnabled, liveAchievementsEnabled]);

  useEffect(() => {
    if (selectedMemberId && !members.some((member) => member.id === selectedMemberId)) {
      setSelectedMemberId(null);
    }
  }, [members, selectedMemberId]);

  const selectedMember = useMemo(
    () => members.find((member) => member.id === selectedMemberId) ?? null,
    [members, selectedMemberId],
  );

const teamAchievementsLabel = language === "pl" ? "Osiagniecia w teamie" : language === "zh" ? "团队成就" : "Team achievements";
  const noTeamAchievementsLabel = language === "pl" ? "Brak osiagniec zespolowych" : language === "zh" ? "暂无团队成绩" : "No team achievements yet";

  const activeMembers = useMemo(() => members.filter((member) => member.isActive), [members]);

  const memberLookupByName = useMemo(() => {
    const lookup = new Map<string, Member>();
    members.forEach((member) => {
      lookup.set(normalizeNameForLookup(member.name), member);
      if (member.nickname) {
        lookup.set(normalizeNameForLookup(member.nickname), member);
      }
    });
    return lookup;
  }, [members]);

  const memberAchievementsById = useMemo(() => {
    const achievementsById = new Map<string, MemberAchievement[]>();

    members.forEach((member) => {
      achievementsById.set(member.id, []);
    });

    achievements.forEach((achievement) => {
      const rank = getRacePositionRank(achievement.position);
      const seenMembers = new Set<string>();

      splitLineup(achievement.winners).forEach((name) => {
        const matchedMember = getLookupNameVariants(name)
          .map((variant) => memberLookupByName.get(variant))
          .find(Boolean);

        if (!matchedMember || seenMembers.has(matchedMember.id)) {
          return;
        }

        seenMembers.add(matchedMember.id);
        achievementsById.get(matchedMember.id)?.push({
          ...achievement,
          rank,
        });
      });
    });

    achievementsById.forEach((value) => {
      value.sort((a, b) => {
        if (a.rank !== b.rank) {
          return a.rank - b.rank;
        }

        const aTime = parseRaceDate(a.date)?.getTime() ?? 0;
        const bTime = parseRaceDate(b.date)?.getTime() ?? 0;
        return bTime - aTime;
      });
    });

    return achievementsById;
  }, [achievements, memberLookupByName, members]);

  const groupedMembers = useMemo(() => {
    const grouped: Record<MemberCategory, Member[]> = {
      management: [],
      lmu: [],
      assetto: [],
    };

    activeMembers.forEach((member) => {
      grouped[member.category].push(member);
    });

    return grouped;
  }, [activeMembers]);

  const managementMembers = groupedMembers.management;
  const driverMembers = useMemo(() => [...groupedMembers.lmu, ...groupedMembers.assetto], [groupedMembers]);
  const selectedMemberAchievements = useMemo(
    () => (selectedMember ? memberAchievementsById.get(selectedMember.id) ?? [] : []),
    [memberAchievementsById, selectedMember],
  );
const selectedMemberMedalAchievements = useMemo(
    () => selectedMemberAchievements.filter((achievement) => achievement.rank <= 3),
    [selectedMemberAchievements],
  );
  const selectedMemberFavoriteCarImage = useMemo(
    () => (selectedMember ? getFavoriteCarImage(selectedMember.favoriteCar) : ""),
    [selectedMember],
  );
  const driversCategoryCopy = useMemo(
    () => ({
      eyebrow: language === "pl" ? "Drivers" : language === "zh" ? "车手" : "Drivers",
      title: language === "pl" ? "Kierowcy" : language === "zh" ? "车手阵容" : "Drivers",
      description:
        language === "pl"
          ? "Zawodnicy reprezentujący GLS PXN Racing w najważniejszych projektach wyścigowych."
          : language === "zh"
            ? "代表 GLS PXN Racing 出战关键赛事项目的车手阵容。"
            : "Drivers representing GLS PXN Racing across our key racing projects.",
    }),
    [language],
  );
  const racesWithTimestamp = useMemo(() => races.map(withRaceTimestamp), [races]);

  const incomingRaces = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayTime = today.getTime();

    return racesWithTimestamp
      .filter((race) => race.timestamp !== null && race.timestamp >= todayTime)
      .sort((a, b) => (a.timestamp ?? Number.MAX_SAFE_INTEGER) - (b.timestamp ?? Number.MAX_SAFE_INTEGER));
  }, [racesWithTimestamp]);

  const nextIncomingRace = incomingRaces[0] ?? null;
  const nextRaceLabel = language === "pl" ? "Następny wyścig" : language === "zh" ? "下一场比赛" : "Next race";
  const nextRaceMetaLabels = language === "pl"
    ? { countdown: "Odliczanie", class: "Klasa", event: "Typ", lineup: "Skład" }
    : language === "zh"
      ? { countdown: "倒计时", class: "组别", event: "赛事", lineup: "阵容" }
      : { countdown: "Countdown", class: "Class", event: "Event", lineup: "Line-up" };
  const nextRaceTargetDate = nextIncomingRace ? parseRaceDate(nextIncomingRace.date) : null;
  const getStreamPlatform = (race: Race | null) => {
    if (!race) return "";
    const source = `${race.streamPlatform} ${race.streamUrl}`.toLowerCase();
    if (source.includes("twitch")) return "twitch";
    if (source.includes("kick")) return "kick";
    if (source.includes("youtube") || source.includes("youtu.be")) return "youtube";
    return "";
  };
  const nextRaceStreamPlatform = getStreamPlatform(nextIncomingRace);
  const nextRaceStreamUrl = nextIncomingRace?.streamUrl?.trim() || "";

  const sortedAchievements = useMemo(
    () =>
      [...achievements].sort((a, b) => {
        const aTime = parseRaceDate(a.date)?.getTime() ?? 0;
        const bTime = parseRaceDate(b.date)?.getTime() ?? 0;
        return bTime - aTime;
      }),
    [achievements],
  );

  const achievementsScrollRef = useRef<HTMLDivElement | null>(null);
  const achievementsDragStateRef = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const scrollAchievements = (direction: -1 | 1) => {
    const container = achievementsScrollRef.current;
    if (!container) return;
    container.scrollBy({ left: Math.round(container.clientWidth * 0.92) * direction, behavior: "smooth" });
  };

  const handleAchievementsPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = achievementsScrollRef.current;
    if (!container || event.pointerType !== "mouse") return;
    achievementsDragStateRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
    };
    container.classList.add("is-dragging");
    container.setPointerCapture(event.pointerId);
  };

  const handleAchievementsPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = achievementsScrollRef.current;
    const dragState = achievementsDragStateRef.current;
    if (!container || !dragState.active) return;
    container.scrollLeft = dragState.scrollLeft - (event.clientX - dragState.startX);
  };


  const stopAchievementsDrag = (event?: React.PointerEvent<HTMLDivElement>) => {
    const container = achievementsScrollRef.current;
    const dragState = achievementsDragStateRef.current;
    if (!container || !dragState.active) return;
    dragState.active = false;
    container.classList.remove("is-dragging");
    if (event && container.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.id) {
            const sectionId = entry.target.id as SectionId;
            setVisibleSections((prev) => (prev[sectionId] ? prev : { ...prev, [sectionId]: true }));
          }
        });

        const visibleEntries = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const topEntry = visibleEntries[0];
        if (topEntry?.target.id) {
          setActiveSection(topEntry.target.id as SectionId);
        }
      },
      {
        rootMargin: "25% 0px -2% 0px",
        threshold: [0, 0.01, 0.05],
      },
    );

    navigation.forEach((item) => {
      const element = document.getElementById(item.key);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (section: SectionId) => {
    document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(section);
    setMobileNavOpen(false);
  };

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (window.innerWidth < 1024) return;
      if (event.deltaY <= 0) return;
      if (window.scrollY > window.innerHeight * 0.35) return;
      if (homeSnapLockedRef.current) return;

      event.preventDefault();
      homeSnapLockedRef.current = true;
      scrollToSection("about");
      window.setTimeout(() => {
        homeSnapLockedRef.current = false;
      }, 900);
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden text-white">
      <header className="fixed inset-x-0 top-[2vh] z-[60] px-3 sm:px-6 lg:top-[5vh] lg:px-8">
        <div className="mx-auto max-w-[1600px] overflow-visible rounded-[1.5rem] border border-white/15 bg-gradient-to-br from-white/10 via-white/5 to-black/45 shadow-[0_35px_120px_-45px_rgba(0,0,0,0.85)] backdrop-blur-md lg:rounded-[1.9rem]">
          <div className="relative flex min-h-[62px] w-full flex-col overflow-visible lg:min-h-[66px]">
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.5rem] lg:rounded-[1.9rem]">
              <div className="absolute -top-16 left-12 h-28 w-52 rounded-full bg-white/12 blur-2xl" />
              <div className="absolute -bottom-10 right-10 h-24 w-40 rounded-full bg-[#dc0000]/25 blur-2xl" />
              <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.14)_0%,transparent_35%,transparent_65%,rgba(255,255,255,0.08)_100%)]" />
            </div>

            <div className="relative z-10 flex items-center justify-between px-3 py-3 lg:hidden">
              <button
                type="button"
                onClick={() => scrollToSection("home")}
                className="flex h-11 w-11 items-center justify-center rounded-[0.95rem] bg-[#dc0000] transition hover:bg-[#b80000]"
              >
                <img src={IMAGE_ASSETS.logos.team} alt="GLS PXN Racing logo" className="h-8 w-8 object-contain" />
              </button>

              <button
                type="button"
                onClick={() => setMobileNavOpen((value) => !value)}
                className="font-display inline-flex min-h-[46px] items-center gap-3 rounded-[0.95rem] border border-white/15 bg-white/10 px-4 py-3 text-sm font-black uppercase tracking-[0.08em] text-white transition hover:bg-white/15"
              >
                <img src={currentLanguageOption.flagSrc} alt={currentLanguageOption.label} className="h-4 w-6 rounded-sm object-cover" />
                <span>Menu</span>
                <span className={`transition ${mobileNavOpen ? "rotate-45" : ""}`}>+</span>
              </button>
            </div>

            {mobileNavOpen && (
              <div className="relative z-10 border-t border-white/10 bg-black/35 p-3 lg:hidden">
                <div className="space-y-2 rounded-[1rem] border border-white/10 bg-black/35 p-2 backdrop-blur-sm">
                  {navigation.map((item) => {
                    const active = activeSection === item.key;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => scrollToSection(item.key)}
                        className={`flex w-full items-center justify-between rounded-[0.85rem] px-4 py-3 text-left text-sm font-bold uppercase tracking-[0.12em] transition ${
                          active ? "bg-[#dc0000] text-white" : "text-zinc-200 hover:bg-white/10"
                        }`}
                      >
                        <span>{item.label[language]}</span>
                      </button>
                    );
                  })}
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => setLanguage(option.code)}
                        className={`flex w-full items-center gap-3 rounded-[0.8rem] px-3 py-3 text-left text-xs font-bold uppercase tracking-[0.14em] transition ${
                          language === option.code ? "bg-[#dc0000] text-white" : "text-zinc-200 hover:bg-white/10"
                        }`}
                      >
                        <img src={option.flagSrc} alt={option.label} className="h-4 w-6 rounded-sm object-cover" />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="relative hidden min-h-[66px] w-full lg:flex lg:flex-row lg:items-stretch">
              <button
                type="button"
                onClick={() => scrollToSection("home")}
                className="relative z-10 flex h-[66px] w-[168px] shrink-0 items-center justify-center rounded-l-[1.4rem] bg-[#dc0000] px-6 transition hover:bg-[#b80000]"
              >
                <img src={IMAGE_ASSETS.logos.team} alt="GLS PXN Racing logo" className="h-12 w-12 object-contain" />
              </button>

              <nav className="relative z-10 flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-6 gap-y-3 bg-white/12 px-4 py-4 sm:gap-x-8 lg:flex-nowrap lg:px-8 lg:py-0">
                {navigation.map((item) => {
                  const active = activeSection === item.key;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => scrollToSection(item.key)}
                      className={`font-display shrink-0 text-sm font-black uppercase tracking-[0.08em] transition lg:text-[0.95rem] ${
                        active ? "text-white" : "text-zinc-100/85 hover:text-white"
                      }`}
                    >
                      {item.label[language]}
                    </button>
                  );
                })}
              </nav>

              <div className="group relative z-10 shrink-0 rounded-r-[1.4rem] bg-[#dc0000]">
                <button
                  type="button"
                  className="font-display flex h-[66px] w-[168px] items-center justify-center gap-3 rounded-r-[1.4rem] px-6 text-sm font-black uppercase tracking-[0.08em] text-white transition hover:bg-[#b80000]"
                >
                  <img src={currentLanguageOption.flagSrc} alt={currentLanguageOption.label} className="h-4 w-6 rounded-sm object-cover" />
                  <span>{currentLanguageOption.label}</span>
                </button>

                <div className="pointer-events-none absolute right-0 top-full w-40 pt-1 opacity-0 transition duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
                  <div className="rounded-[1rem] border border-white/10 bg-[#202224]/96 p-1 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => setLanguage(option.code)}
                        className={`flex w-full items-center gap-3 rounded-[0.8rem] px-3 py-3 text-left text-xs font-bold uppercase tracking-[0.14em] transition ${
                          language === option.code ? "bg-[#dc0000] text-white" : "text-zinc-200 hover:bg-white/10"
                        }`}
                      >
                        <img src={option.flagSrc} alt={option.label} className="h-4 w-6 rounded-sm object-cover" />
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto mt-0 w-full max-w-[1600px] pb-6 pt-0 sm:pb-8 sm:pt-0 lg:pb-12 lg:pt-0">


        <section id="home" className={`fog-reveal ${visibleSections.home ? "is-visible" : ""} scroll-mt-28 relative left-1/2 w-screen -translate-x-1/2 min-h-screen overflow-hidden bg-cover bg-center bg-no-repeat flex items-center`} style={getSectionBackgroundStyle("home", isPortrait)}>

          <div className="mx-auto grid w-full max-w-[1600px] content-center gap-8 px-4 py-4 pt-[16vh] text-center sm:px-0 sm:py-6 sm:pt-[13vh] lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:px-0 lg:pt-[5vh] lg:text-left">
          <div className="flex max-w-4xl flex-col items-center justify-center text-center lg:items-start lg:text-left">

            <h1 className="mt-0 text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl xl:text-7xl">
              {t.heroTitleTop}
              <span className="mt-3 block text-[#dc0000]">{t.heroTitleBottom}</span>
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-7 text-zinc-300 sm:mt-6 sm:text-lg sm:leading-8">{t.heroText}</p>
            <p className="mt-5 max-w-3xl text-sm uppercase tracking-[0.2em] text-zinc-500">{t.sectionHint}</p>
          </div>

          <div className="relative mx-auto w-[70%] min-w-[280px] max-w-[520px] lg:mr-0 lg:ml-auto">
            <div className="absolute -left-10 top-0 h-40 w-40 rounded-full bg-[#dc0000]/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-br from-white/10 via-white/5 to-black/40 p-4 backdrop-blur-md sm:p-6 shadow-[0_35px_120px_-45px_rgba(0,0,0,0.85)]">
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.9rem]">
                <div className="absolute -top-16 left-8 h-28 w-44 rounded-full bg-white/15 blur-2xl" />
                <div className="absolute -bottom-10 right-8 h-24 w-36 rounded-full bg-[#dc0000]/25 blur-2xl" />
                <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.16)_0%,transparent_35%,transparent_65%,rgba(255,255,255,0.08)_100%)]" />
              </div>
              <div className="relative z-10 rounded-[1.7rem] bg-white/5 p-4 sm:p-6">
                <div className="border-b border-white/10 pb-4 text-center">
                  <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-zinc-300">{t.dashboardBadge}</p>
                </div>
                <div className="mt-4 min-h-[25rem] rounded-[1.2rem] border border-white/15 bg-black/30 px-4 py-4">
                  <p className="font-display min-h-[0.9rem] text-[10px] font-bold uppercase tracking-[0.18em] text-[#dc0000] sm:text-[11px] sm:tracking-[0.24em] whitespace-nowrap">{nextRaceLabel}</p>
                  {nextIncomingRace ? (
                    <>
                      <div className="mt-3 flex min-h-[3.25rem] items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-display text-base font-black uppercase tracking-[0.08em] text-white">{nextIncomingRace.raceTitle || "-"}</p>
                          <p className="font-display mt-1 text-[0.68rem] font-bold uppercase tracking-[0.22em] text-zinc-400">
                            {nextIncomingRace.leagueName || "-"}
                          </p>
                        </div>
                        {nextRaceStreamPlatform && nextRaceStreamUrl ? (
                          <a
                            href={nextRaceStreamUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`Watch ${nextIncomingRace.raceTitle} stream`}
                            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition ${getStreamButtonClass(nextRaceStreamPlatform)}`}
                          >
                            {renderStreamIcon(nextRaceStreamPlatform)}
                          </a>
                        ) : <div className="h-9 w-9 shrink-0" aria-hidden="true" />} 
                      </div>

                      <div className="mt-4 min-h-[5.4rem] border-t border-white/10 pt-4">
                        <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-500">{nextRaceMetaLabels.countdown}</p>
                        <p className="font-display mt-2 text-[1.65rem] font-black leading-none text-white sm:text-[1.85rem]">
                          <RaceCountdown targetDate={nextRaceTargetDate} />
                        </p>
                      </div>

                      <div className="mt-4 grid min-h-[5.8rem] gap-4 border-t border-white/10 pt-4 sm:grid-cols-2">
                        <div>
                          <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.24em] text-zinc-500">{nextRaceMetaLabels.class}</p>
                          <p className="font-display mt-2 text-xs font-black uppercase tracking-[0.16em] text-white">{formatRaceMeta(nextIncomingRace.carClass)}</p>
                        </div>
                        <div>
                          <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.24em] text-zinc-500">{nextRaceMetaLabels.event}</p>
                          <p className="font-display mt-2 text-xs font-black uppercase tracking-[0.16em] text-white">{formatRaceMeta(nextIncomingRace.eventType)}</p>
                        </div>
                      </div>

                      <div className="mt-4 min-h-[6.75rem] border-t border-white/10 pt-4">
                        {nextIncomingRace.participants ? (
                          <>
                            <p className="font-display text-[0.58rem] font-bold uppercase tracking-[0.24em] text-zinc-500">{nextRaceMetaLabels.lineup}</p>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {splitLineup(nextIncomingRace.participants).map((name, index) => {
                                const isLead = index < 3;
                                const matchedMember = getLookupNameVariants(name).map((variant) => memberLookupByName.get(variant)).find(Boolean);
                                const className = `flex items-center gap-2 rounded-[0.95rem] border border-white/10 bg-black/35 px-2.5 py-1.5 font-display text-[0.6rem] font-bold uppercase tracking-[0.14em] text-white ${
                                  isLead ? "shadow-[0_16px_40px_-28px_rgba(220,0,0,0.55)]" : ""
                                } ${matchedMember ? "ui-hover-button hover:bg-[#dc0000]" : ""}`;

                                if (matchedMember) {
                                  return (
                                    <button
                                      key={`${name}-${index}`}
                                      type="button"
                                      onPointerDown={(event) => event.stopPropagation()}
                                      onClick={() => setSelectedMemberId(matchedMember.id)}
                                      className={className}
                                      title={t.openProfile}
                                    >
                                      <span className={`font-display text-[0.55rem] ${isLead ? "text-white" : "text-zinc-200"}`}>{name}</span>
                                    </button>
                                  );
                                }

                                return (
                                  <div key={`${name}-${index}`} className={className}>
                                    <span className={`font-display text-[0.55rem] ${isLead ? "text-white" : "text-zinc-200"}`}>{name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </>
                  ) : isDataReady ? (
                    <div className="mt-3 min-h-[20.5rem]" />
                  ) : (
                    <div className="mt-3 space-y-4" aria-hidden="true">
                      <div className="flex min-h-[3.25rem] items-start justify-between gap-4">
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="h-5 w-3/4 rounded-full bg-white/10" />
                          <div className="h-3 w-1/2 rounded-full bg-white/8" />
                        </div>
                        <div className="h-9 w-9 shrink-0 rounded-[0.95rem] bg-white/8" />
                      </div>
                      <div className="min-h-[5.4rem] border-t border-white/10 pt-4">
                        <div className="h-3 w-24 rounded-full bg-white/8" />
                        <div className="mt-3 h-9 w-40 rounded-full bg-white/10" />
                      </div>
                      <div className="grid min-h-[5.8rem] gap-4 border-t border-white/10 pt-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <div className="h-3 w-16 rounded-full bg-white/8" />
                          <div className="h-4 w-20 rounded-full bg-white/10" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 w-16 rounded-full bg-white/8" />
                          <div className="h-4 w-20 rounded-full bg-white/10" />
                        </div>
                      </div>
                      <div className="min-h-[6.75rem] border-t border-white/10 pt-4">
                        <div className="h-3 w-16 rounded-full bg-white/8" />
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <div className="h-8 w-24 rounded-[0.95rem] bg-white/10" />
                          <div className="h-8 w-20 rounded-[0.95rem] bg-white/8" />
                          <div className="h-8 w-28 rounded-[0.95rem] bg-white/8" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
</section>

        <section id="about" className={`fog-reveal ${visibleSections.about ? "is-visible" : ""} scroll-mt-32 mt-16 relative overflow-hidden rounded-[2rem] border border-white/15 bg-cover bg-center bg-no-repeat p-4 sm:p-6 space-y-10 md:mt-24`} style={getSectionBackgroundStyle("about", isPortrait)}>

          <div className="grid gap-10 xl:grid-cols-[0.95fr_1.05fr] 2xl:gap-14">
            <div className="p-6 sm:p-8">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.aboutEyebrow}</p>
              <h2 className="mt-3 text-3xl font-black uppercase leading-tight text-white sm:mt-4 sm:text-4xl">{t.aboutTitle}</h2>
              <p className="mt-5 text-base leading-8 text-zinc-300">{t.aboutText}</p>
              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="ui-hover-button mt-6 inline-flex items-center justify-center rounded-full border border-white/10 bg-[#dc0000] px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-[#b80000]"
              >
                {language === "pl" ? "Przejdź do social mediów" : language === "zh" ? "前往社交媒体" : "Go to social media"}
              </button>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {t.aboutCards.filter((card) => card.title?.trim() && card.text?.trim()).slice(-3).map((card) => (
                <div key={card.title} className="ui-hover-card rounded-[1.8rem] border border-white/15 bg-black/30 backdrop-blur-sm p-6">
                  <h3 className="text-xl font-black uppercase text-white">{card.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-zinc-400">{card.text}</p>
                </div>
              ))}
            </div>
          </div>
</section>

        <section id="partners" className={`fog-reveal ${visibleSections.partners ? "is-visible" : ""} scroll-mt-32 mt-16 rounded-[2rem] border border-white/15 bg-cover bg-center bg-no-repeat backdrop-blur-md p-6 sm:p-8 md:mt-24 lg:p-10`} style={getSectionBackgroundStyle("partners", isPortrait)}>

          <div className="grid gap-10 xl:grid-cols-[0.9fr_1.1fr] xl:items-start 2xl:gap-14">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.partnersEyebrow}</p>
              <h2 className="mt-3 text-3xl font-black uppercase leading-tight text-white sm:mt-4 sm:text-4xl">{t.partnersTitle}</h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-zinc-300">{t.partnersText}</p>
              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="ui-hover-button mt-6 inline-flex items-center justify-center rounded-full border border-white/10 bg-[#dc0000] px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-[#b80000]"
              >
                {language === "pl" ? "Zostań sponsorem" : language === "zh" ? "成为赞助商" : "Become a sponsor"}
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {partners.map((partner) => (
                <a
                  key={partner.name}
                  href={partner.website}
                  target="_blank"
                  rel="noreferrer"
                  className="ui-hover-card group relative flex min-h-48 flex-col justify-between rounded-[1.7rem] border border-white/15 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(0,0,0,0.26))] backdrop-blur-sm p-5"
                >
                  <div className="partner-logo-shell flex min-h-[11.2rem] items-center justify-center rounded-[1.35rem] border border-white/12 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.14),rgba(255,255,255,0.04)_42%,rgba(0,0,0,0.18)_100%)] px-7 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] transition duration-300 group-hover:border-[#dc0000]/30 group-hover:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),rgba(255,255,255,0.05)_42%,rgba(0,0,0,0.22)_100%)]">
                    {partner.image ? (
                      <img src={partner.image} alt={partner.name} className="partner-logo h-full max-h-[7rem] w-full object-contain transition duration-300 group-hover:scale-[1.02]" />
                    ) : (
                      <div className="px-4 text-center text-lg font-black uppercase tracking-[0.22em] text-white">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500">
                          {partner.name === "PXN"
                            ? language === "pl"
                              ? "Sponsor tytularny"
                              : language === "zh"
                                ? "冠名赞助商"
                                : "Title sponsor"
                            : t.partnerPlaceholder}
                        </div>
                        <div className="mt-2">{partner.name}</div>
                      </div>
                    )}
                  </div>

                  {copiedPromo?.code === partner.promoCode && (
                    <div
                      className="pointer-events-none fixed z-[60] rounded-full border border-white/10 bg-black/80 px-3 py-1 text-[0.62rem] font-bold uppercase tracking-[0.22em] text-[#dc0000] shadow-[0_10px_30px_-16px_rgba(0,0,0,0.6)]"
                      style={{ left: copiedPromo.x + 12, top: copiedPromo.y + 12 }}
                    >
                      {promoCopiedLabel}
                    </div>
                  )}
                  {partner.promoCode && (
                    <div className="mt-4 flex flex-wrap items-center gap-2 rounded-[1rem] border border-white/10 bg-black/35 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/90">
                      <span className="text-zinc-400">{partner.promoLabel ? partner.promoLabel[language] : "Promo"}</span>
                      {partner.promoDiscount && <span className="text-[#dc0000]">{partner.promoDiscount}</span>}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handlePromoCopy(partner.promoCode, { x: event.clientX, y: event.clientY });
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
                            handlePromoCopy(partner.promoCode, { x: rect.right, y: rect.top });
                          }
                        }}
                        className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[0.62rem] tracking-[0.22em] text-white/90 hover:bg-white/10 cursor-pointer"
                      >
                        {partner.promoCode}
                      </span>
                    </div>
                  )}
                  <div className="mt-4">
                    <p className="text-sm leading-6 text-zinc-300">{partner.description[language]}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
</section>

        <section id="team" className={`fog-reveal ${visibleSections.team ? "is-visible" : ""} scroll-mt-32 mt-16 relative overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-br from-white/10 via-white/5 to-black/40 p-4 backdrop-blur-md shadow-[0_35px_120px_-45px_rgba(0,0,0,0.85)] sm:p-6 md:mt-24`}>

          <div className="mb-8 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.teamEyebrow}</p>
              <h2 className="mt-3 text-3xl font-black uppercase text-white sm:text-4xl">{t.teamTitle}</h2>
            </div>
            <p className="max-w-2xl text-zinc-400">{t.teamText}</p>
          </div>

          {activeMembers.length === 0 ? (
            <div className="rounded-[2rem] border border-white/15 bg-white/10 backdrop-blur-sm p-8 text-center text-sm font-medium text-zinc-300">{t.noMembers}</div>
          ) : (
            <div className="space-y-10">
              <section className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 backdrop-blur-sm p-6">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{ backgroundImage: `url(${TEAM_CATEGORY_BG_IMAGES.management})` }} />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#dc0000]/55" />
                </div>

                <div className="relative z-10">
                  <div className="mb-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#dc0000]">{t.categories.management.eyebrow}</p>
                      <h3 className="mt-2 text-3xl font-black uppercase text-white">{t.categories.management.title}</h3>
                    </div>
                    <p className="max-w-2xl text-sm leading-7 text-zinc-400">{t.categories.management.description}</p>
                  </div>

<div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {managementMembers.map((member) => {
                      const memberAchievements = memberAchievementsById.get(member.id) ?? [];
                      const topAchievement = memberAchievements[0];

                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => setSelectedMemberId(member.id)}
                          className="ui-hover-card group overflow-hidden rounded-[1.8rem] border border-white/15 bg-black/30 backdrop-blur-sm p-5 text-left hover:bg-white/5"
                        >
                          <div className="overflow-hidden rounded-[1.3rem] border border-white/10 bg-zinc-950">
                            <div className="aspect-[4/4.4] w-full">
                              <img src={getMemberImage(member)} alt={member.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                            </div>
                          </div>
                          <div className="mt-5">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#dc0000]">{getMemberRole(member, language)}</p>
                            <h4 className="mt-2 text-2xl font-black text-white">{member.name}</h4>
                          </div>

                          <div className="mt-5 min-h-[3.75rem]">
                            {getMemberSocialLinks(member).length > 0 && (
                              <div className="grid grid-cols-2 gap-3">
                                {getMemberSocialLinks(member).map((social) => (
                                  <a
                                    key={member.id + "-" + social.label}
                                    href={social.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    className="ui-hover-button inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-sm px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-[#dc0000]"
                                  >
                                    {social.label}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 backdrop-blur-sm p-6">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{ backgroundImage: `url(${RACES_SECTION_BG_IMAGE})` }} />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#dc0000]/55" />
                </div>

                <div className="relative z-10">

                  <div className="mb-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#dc0000]">{driversCategoryCopy.eyebrow}</p>
                      <h3 className="mt-2 text-3xl font-black uppercase text-white">{driversCategoryCopy.title}</h3>
                    </div>
                    <p className="max-w-2xl text-sm leading-7 text-zinc-400">{driversCategoryCopy.description}</p>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {driverMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => setSelectedMemberId(member.id)}
                        className="ui-hover-card group overflow-hidden rounded-[1.8rem] border border-white/15 bg-black/30 backdrop-blur-sm p-5 text-left hover:bg-white/5"
                      >
                        <div className="overflow-hidden rounded-[1.3rem] border border-white/10 bg-zinc-950">
                          <div className="aspect-[4/4.4] w-full">
                            <img src={getMemberImage(member)} alt={member.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                          </div>
                        </div>
                        <div className="mt-5">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#dc0000]">{getMemberRole(member, language)}</p>
                          <h4 className="mt-2 text-2xl font-black text-white">{member.name}</h4>
                        </div>

                        <div className="mt-5 min-h-[3.75rem]">
                          {getMemberSocialLinks(member).length > 0 && (
                            <div className="grid grid-cols-2 gap-3">
                              {getMemberSocialLinks(member).map((social) => (
                                <a
                                  key={`${member.id}-${social.label}`}
                                  href={social.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  className="ui-hover-button inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-sm px-4 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white hover:bg-[#dc0000]"
                                >
                                  {social.label}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>

                     </button>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}
        </section>

        <section id="achievements" className={`fog-reveal ${visibleSections.achievements ? "is-visible" : ""} scroll-mt-32 mt-16 relative overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-br from-white/10 via-white/5 to-black/40 p-4 backdrop-blur-md shadow-[0_35px_120px_-45px_rgba(0,0,0,0.85)] sm:p-6 md:mt-24`}>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 backdrop-blur-sm p-6 sm:p-8">
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-18" style={{ backgroundImage: `url(${RACES_SECTION_BG_IMAGE})` }} />
              <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#dc0000]/45" />
            </div>

            <div className="relative z-10">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.achievementsEyebrow}</p>
              <h2 className="mt-3 text-3xl font-black uppercase leading-tight text-white sm:mt-4 sm:text-4xl">{t.achievementsTitle}</h2>
              <p className="mt-5 max-w-3xl text-base leading-8 text-zinc-300">{t.achievementsText}</p>

              {sortedAchievements.length > 0 ? (
                <div className="mt-8">
                  <div className="mb-4 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => scrollAchievements(-1)}
                      className="ui-hover-button inline-flex h-11 w-11 items-center justify-center rounded-[0.95rem] border border-white/10 bg-white/5 text-lg font-black text-white hover:bg-[#dc0000]"
                      aria-label="Scroll achievements left"
                    >
                      &lt;
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollAchievements(1)}
                      className="ui-hover-button inline-flex h-11 w-11 items-center justify-center rounded-[0.95rem] border border-white/10 bg-white/5 text-lg font-black text-white hover:bg-[#dc0000]"
                      aria-label="Scroll achievements right"
                    >
                      &gt;
                    </button>
                  </div>
                  <div
                    ref={achievementsScrollRef}
                    onPointerDown={handleAchievementsPointerDown}
                    onPointerMove={handleAchievementsPointerMove}
                    onPointerUp={stopAchievementsDrag}
                    onPointerCancel={stopAchievementsDrag}
                    onPointerLeave={stopAchievementsDrag}
                    className="achievements-strip grid cursor-grab grid-flow-col auto-cols-[85%] gap-4 overflow-x-auto pb-3 pr-2 snap-x snap-proximity select-none md:auto-cols-[calc(50%-0.5rem)] xl:auto-cols-[calc((100%-2rem)/3)]"
                  >
                    {sortedAchievements.map((achievement) => {
                      const positionClass = getRacePositionClass(achievement.position);

                      return (
                        <article
                          key={achievement.id}
                          className={`ui-hover-card relative snap-start overflow-hidden rounded-[1.6rem] border p-6 backdrop-blur-sm shadow-[0_35px_120px_-45px_rgba(0,0,0,0.85)] ${getRaceEventCardClass(achievement.position)}`}
                        >
                          {achievement.background ? (
                            <div className="absolute inset-0">
                              <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" style={{ backgroundImage: `url(${achievement.background})` }} />
                              <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#dc0000]/55" />
                            </div>
                          ) : null}

                          <div className="relative z-10">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#dc0000]">{achievement.category || achievement.game || t.achievementsEyebrow}</p>
                                <h3 className="mt-3 min-h-[2.6em] text-2xl font-black leading-[1.3] text-white">{achievement.eventName}</h3>
                              </div>
                              <div className={`rounded-[1.1rem] border px-4 py-3 text-right ${getRacePositionBadgeClass(achievement.position)}`}>
                                <div className="text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-400">Position</div>
                                <div className={`mt-1 text-3xl ${positionClass}`}>{formatRacePosition(achievement.position)}</div>
                              </div>
                            </div>

                            <div className="mt-5 flex min-h-[112px] flex-col justify-center rounded-[1.2rem] border border-white/10 bg-white/5 px-4 py-4">
                              <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-500">League / Event</p>
                              <p className="mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-black uppercase tracking-[0.14em] text-white">{achievement.leagueName || achievement.eventName}</p>
                            </div>

                            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(88px,0.6fr)] sm:auto-rows-fr">
                              <div className="flex h-full min-h-[104px] flex-col justify-center rounded-[1.2rem] border border-white/10 bg-white/5 px-4 py-4">
                                <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-500">Car</p>
                                <p className="mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-black uppercase tracking-[0.14em] text-white">{achievement.car || "-"}</p>
                              </div>
                              <div className="flex h-full min-h-[104px] flex-col justify-center rounded-[1.2rem] border border-white/10 bg-white/5 px-4 py-4 text-center">
                                <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-500">Game</p>
                                <p className="mt-2 text-sm font-black uppercase tracking-[0.14em] text-white">{achievement.game || "-"}</p>
                              </div>
                            </div>

                            <div className="mt-4 rounded-[1.2rem] border border-white/10 bg-black/25 px-4 py-4">
                              <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.24em] text-zinc-500">Line-up / Driver</p>
                              {achievement.winners ? (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  {splitLineup(achievement.winners).map((name, index) => {
                                    const isLead = index < 3;
                                    const matchedMember = getLookupNameVariants(name).map((variant) => memberLookupByName.get(variant)).find(Boolean);
                                    const className = `flex items-center gap-2 rounded-[0.95rem] border border-white/10 bg-black/35 px-2.5 py-1.5 font-display text-[0.6rem] font-bold uppercase tracking-[0.14em] text-white ${
                                      isLead ? "shadow-[0_16px_40px_-28px_rgba(220,0,0,0.55)]" : ""
                                    } ${matchedMember ? "ui-hover-button hover:bg-[#dc0000]" : ""}`;

                                    if (matchedMember) {
                                      return (
                                        <button
                                          key={`${name}-${index}`}
                                          type="button"
                                          onPointerDown={(event) => event.stopPropagation()}
                                          onClick={() => setSelectedMemberId(matchedMember.id)}
                                          className={className}
                                          title={t.openProfile}
                                        >
                                          <span className={`font-display text-[0.55rem] ${isLead ? "text-white" : "text-zinc-200"}`}>{name}</span>
                                        </button>
                                      );
                                    }

                                    return (
                                      <div key={`${name}-${index}`} className={className}>
                                        <span className={`font-display text-[0.55rem] ${isLead ? "text-white" : "text-zinc-200"}`}>{name}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                              <p className="mt-2 text-sm leading-7 text-zinc-200">-</p>
                            )}
                          </div>

                          <div className="mt-4 flex items-center justify-end gap-4 text-xs font-bold uppercase tracking-[0.22em] text-zinc-500">
                            <span>{achievement.date || "-"}</span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  </div>
                </div>
              ) : (
                <div className="mt-8 rounded-[1.6rem] border border-white/15 bg-black/30 p-6 text-sm text-zinc-300 backdrop-blur-sm">
                  {t.noAchievements}
                </div>
              )}
            </div>
          </div>
        </section>
        <section id="contact" className={`fog-reveal ${visibleSections.contact ? "is-visible" : ""} scroll-mt-32 mt-16 p-4 sm:p-6 md:mt-24`}>

          <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.contactEyebrow}</p>
          <h2 className="mt-3 text-3xl font-black uppercase leading-tight text-white sm:mt-4 sm:text-4xl">{t.contactTitle}</h2>
          <p className="mt-5 text-base leading-8 text-zinc-300">{t.contactText}</p>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {t.contactSegments.map((segment) => (
              <div key={segment.title} className="ui-hover-card rounded-[1.8rem] border border-white/15 bg-black/30 p-6 backdrop-blur-sm">
                <h3 className="text-xl font-black uppercase text-white">{segment.title}</h3>
                <p className="mt-3 text-sm leading-7 text-zinc-300">{segment.text}</p>
                {segment.kind === "drivers" ? (
                  <form className="mt-5 grid gap-3" onSubmit={handleDriverFormSubmit}>
                    <label className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-[#dc0000]">{t.driverFormDiscordLabel}</label>
                    <input
                      value={driverForm.discordId}
                      onChange={updateDriverFormField("discordId")}
                      required
                      className="w-full rounded-[0.9rem] border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-zinc-500"
                    />
                    <label className="mt-2 text-[0.6rem] font-bold uppercase tracking-[0.22em] text-[#dc0000]">{t.driverFormPlatformLabel}</label>
                    <input
                      value={driverForm.platform}
                      onChange={updateDriverFormField("platform")}
                      required
                      className="w-full rounded-[0.9rem] border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-zinc-500"
                    />
                    <label className="mt-2 text-[0.6rem] font-bold uppercase tracking-[0.22em] text-[#dc0000]">{t.driverFormBioLabel}</label>
                    <textarea
                      value={driverForm.bio}
                      onChange={updateDriverFormField("bio")}
                      required
                      rows={4}
                      className="w-full rounded-[0.9rem] border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-zinc-500"
                    />
                    <button
                      type="submit"
                      disabled={driverFormStatus === "submitting"}
                      className="ui-hover-button mt-2 inline-flex items-center justify-center rounded-full border border-white/10 bg-[#dc0000] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#b80000] disabled:cursor-wait disabled:opacity-60"
                    >
                      {driverFormStatus === "submitting" ? t.driverFormSending : t.driverFormSubmit}
                    </button>
                    {driverFormMessage ? (
                      <p
                        className={"text-xs font-bold uppercase tracking-[0.2em] " + (driverFormStatus === "success" ? "text-emerald-300" : "text-rose-300")}
                        role="status"
                      >
                        {driverFormMessage}
                      </p>
                    ) : null}
                  </form>
                ) : (
                  <a
                    href={segment.ctaHref}
                    target="_blank"
                    rel="noreferrer"
                    className="ui-hover-button mt-5 inline-flex items-center justify-center rounded-full border border-white/10 bg-[#dc0000] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#b80000]"
                  >
                    {segment.ctaLabel}
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {t.contactLinks.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="ui-hover-button inline-flex items-center justify-center rounded-full border border-white/10 bg-[#dc0000] px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white hover:bg-[#b80000]"
              >
                {item.label}
              </a>
            ))}
          </div>
        </section>

        <footer className="mt-16 border-t border-white/10 pt-8 text-center text-xs font-bold uppercase tracking-[0.24em] text-zinc-500 md:mt-24 md:pt-10">
          {t.footerNote}
        </footer>
      </main>

      {selectedMember && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setSelectedMemberId(null)}>
          <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/15 bg-black/55 backdrop-blur-md shadow-[0_30px_120px_-40px_rgba(0,0,0,0.95)] origin-center" style={{ transform: "scale(0.9)" }} onClick={(event) => event.stopPropagation()}>
            <div className="grid gap-0 lg:grid-cols-[0.8fr_1.2fr]">
              <div className="border-b border-white/10 bg-gradient-to-br from-zinc-950 via-black to-[#120202] p-6 lg:border-b-0 lg:border-r">
                <div className="overflow-hidden rounded-[1.7rem] border border-white/10 bg-black shadow-[0_24px_80px_-36px_rgba(0,0,0,0.95)]">
                  <div className="aspect-[4/4.4]">
                    <img src={getMemberImage(selectedMember)} alt={selectedMember.name} className="h-full w-full object-cover" />
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="flex min-h-[96px] flex-col justify-between rounded-[1.25rem] border border-[#dc0000]/20 bg-[linear-gradient(135deg,rgba(220,0,0,0.14),rgba(255,255,255,0.04))] px-4 py-4 shadow-[0_18px_50px_-30px_rgba(220,0,0,0.45)]">
                    <p className="text-[0.62rem] font-bold uppercase tracking-[0.26em] text-[#dc0000]">{t.racingNumber}</p>
                    <p className="mt-2 text-4xl font-black leading-none text-white/90">{formatMemberStat(formatRacingNumber(selectedMember.racingNumber))}</p>
                  </div>
                  <div className="flex min-h-[96px] flex-col justify-between rounded-[1.25rem] border border-white/10 bg-black/35 px-4 py-4 backdrop-blur-sm">
                    <p className="text-[0.62rem] font-bold uppercase tracking-[0.26em] text-[#dc0000]">{teamAchievementsLabel}</p>
                    {selectedMemberMedalAchievements.length > 0 ? (
                      <div className="achievements-strip mt-2 flex gap-2 overflow-x-auto pb-1 pr-1">
                        {selectedMemberMedalAchievements.map((achievement, index) => (
                          <span
                            key={selectedMember.id + "-medal-" + achievement.id + "-" + index}
                            title={getAchievementMedal(achievement.rank) + " " + getAchievementHeadline(achievement)}
                            className={"inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-lg shadow-[0_10px_30px_-18px_rgba(0,0,0,0.7)] " + getAchievementMedalClass(achievement.rank)}
                          >
                            {getAchievementMedal(achievement.rank)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-sm font-black text-white">{noTeamAchievementsLabel}</p>
                    )}
                  </div>
                  <div className="flex min-h-[96px] flex-col justify-between rounded-[1.25rem] border border-white/10 bg-black/35 px-4 py-4 backdrop-blur-sm">
                    <p className="text-[0.62rem] font-bold uppercase tracking-[0.26em] text-[#dc0000]">Role</p>
                    <p className="mt-2 text-sm font-black uppercase tracking-[0.16em] text-white">{getMemberRole(selectedMember, language)}</p>
                  </div>
                  <div className="flex min-h-[96px] flex-col justify-between rounded-[1.25rem] border border-white/10 bg-black/35 px-4 py-4 backdrop-blur-sm">
                    <p className="text-[0.62rem] font-bold uppercase tracking-[0.26em] text-[#dc0000]">{t.favoriteTrack}</p>
                    <p className="mt-2 text-sm font-black text-white">{formatMemberStat(selectedMember.favoriteTrack)}</p>
                  </div>
                </div>

                <div className="mt-2 overflow-hidden rounded-[1.25rem] border border-white/10 bg-black/35 backdrop-blur-sm">
                  <div className="relative min-h-[140px]">
                    <div className="absolute right-4 top-4 z-10 flex max-w-[70%] flex-col items-end gap-2 text-right">
                      <p className="rounded-full border border-white/10 bg-black/55 px-3 py-1.5 text-[0.62rem] font-bold uppercase tracking-[0.22em] text-[#dc0000] backdrop-blur-sm">
                        {t.favoriteCar}
                      </p>
                    </div>
                    {selectedMemberFavoriteCarImage ? (
                      <img
                        src={selectedMemberFavoriteCarImage}
                        alt={selectedMember.favoriteCar || selectedMember.name + " car"}
                        className="h-full min-h-[140px] w-full object-contain"
                      />
                    ) : (
                      <div className="flex min-h-[140px] items-center justify-center px-4 py-3 text-center">
                        <div>
                          <p className="mt-2 text-[0.62rem] font-bold uppercase tracking-[0.22em] text-zinc-500">No image</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-6">
                <div className="relative h-full overflow-hidden rounded-[1.7rem] border border-white/15 bg-gradient-to-br from-white/10 via-white/5 to-black/45 p-6 shadow-[0_24px_80px_-36px_rgba(0,0,0,0.85)] backdrop-blur-md">
                  <div className="pointer-events-none absolute -right-12 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-[#dc0000]/20 blur-3xl" />
                  <div className="relative z-10">
                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.32em] text-[#dc0000]">{t.profileTitle}</p>
                    <div className="flex min-h-[96px] items-end border-b border-white/10 pb-6">
                      <h3 className="max-w-[10ch] text-[clamp(2rem,4vw,3.1rem)] font-black uppercase leading-[0.95] text-white">{selectedMember.name}</h3>
                    </div>

                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                      <div className="flex min-h-[116px] flex-col justify-between rounded-[1.35rem] border border-white/10 bg-black/25 p-5 backdrop-blur-sm">
                        <p className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#dc0000]">{t.nickname}</p>
                        <p className="mt-3 text-2xl font-black text-white">{formatMemberStat(selectedMember.nickname)}</p>
                      </div>
                      <div className="flex min-h-[116px] flex-col justify-between rounded-[1.35rem] border border-white/10 bg-black/25 p-5 backdrop-blur-sm">
                        <p className="text-[0.68rem] font-bold uppercase tracking-[0.28em] text-[#dc0000]">{t.teamSince}</p>
                        <p className="mt-3 text-2xl font-black text-white">{formatMemberStat(selectedMember.teamSince)}</p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-[1.45rem] border border-[#dc0000]/20 bg-[linear-gradient(135deg,rgba(220,0,0,0.16),rgba(255,255,255,0.04))] p-5 shadow-[0_20px_60px_-32px_rgba(220,0,0,0.45)]">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.3em] text-[#dc0000]">{t.strengths}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {getMemberHighlights(selectedMember, language).length > 0 ? (
                          getMemberHighlights(selectedMember, language).map((item) => (
                            <span key={item} className="rounded-full border border-white/10 bg-black/45 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white">
                              {item}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-zinc-300">-</span>
                        )}
                      </div>
                    </div>


                    <div className="mt-5 rounded-[1.45rem] border border-white/10 bg-black/25 p-5 backdrop-blur-sm">
                      <p className="text-[0.68rem] font-bold uppercase tracking-[0.3em] text-[#dc0000]">Bio</p>
                      <p className="mt-4 text-sm leading-8 text-zinc-300">{getMemberBio(selectedMember, language) || "-"}</p>
                    </div>

                    <div className="mt-6 min-h-[3.75rem]">
                      {getMemberSocialLinks(selectedMember).length > 0 && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {getMemberSocialLinks(selectedMember).map((social) => (
                            <a
                              key={selectedMember.id + "-" + social.label}
                              href={social.href}
                              target="_blank"
                              rel="noreferrer"
                              className="ui-hover-button inline-flex items-center justify-center rounded-full border border-white/10 bg-white/10 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white transition hover:bg-[#dc0000]"
                            >
                              {social.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}















































































































































































































































